import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  Background,
  Controls,
  ReactFlow,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
  type Connection,
  type Edge,
  type Node,
} from '@xyflow/react';
import { v4 as uuidv4 } from 'uuid';
import { App } from 'antd';

import '@xyflow/react/dist/style.css';

import FlowNode from './FlowNode';
import { AntIcon } from '../../../components';
import ComponentRenderer from '../../ComponentRenderer';
import { parseJSONSchema } from '../../utils/data-parser';
import { PageProvider, PageMode } from '../../context/PageContext';
import type { IComponentSchema } from '../../types';
import {
  deriveGraph,
  buildPreviewGraph,
  type IDeriveConfig,
  type IRoleVisual,
} from './deriveGraph';
import {
  getPath,
  setPath,
  toRuntimeTemplates,
  type FlowNodeType,
  type IFlowNodeData,
} from './transform';

const nodeTypes = { flowNode: FlowNode };
type Obj = Record<string, unknown>;

export interface IPreviewFlowChartProps {
  /** The whole document ($mainForm) — deriveGraph reads resources + the MBO list. */
  mainForm: Obj;
  config: IDeriveConfig;
  /** role → visual (color / icon / label). */
  visuals?: Record<string, IRoleVisual>;
  /** Display sub-schema rendered inside each node ($node / $row scope). */
  nodeCardSchema?: IComponentSchema<Record<string, unknown>>;
  /** Display sub-schema for the side panel ($node / $row / $sides scope). */
  panelSchema?: IComponentSchema<Record<string, unknown>>;
  /** Persisted relatedNode entry ids (from initial load) → soft-delete on remove. */
  originalRelationIds?: Set<string>;
  /** Write back the MBO array (positions + relatedNode edits). */
  onChangeOperations: (next: Obj[]) => void;
  height?: number | string;
  panelWidth?: number | string;
}

const PreviewInner = ({
  mainForm,
  config,
  visuals,
  nodeCardSchema,
  panelSchema,
  originalRelationIds,
  onChangeOperations,
  height,
  panelWidth,
}: IPreviewFlowChartProps) => {
  const { message } = App.useApp();
  const idField = config.idField ?? 'id';
  const posField = config.positionField ?? 'position';
  const relField = config.relationField ?? 'relatedNode';
  const relTgt = config.relationTargetPath ?? 'node.id';

  const derived = useMemo(() => deriveGraph(mainForm, config), [mainForm, config]);
  const graph = useMemo(
    () =>
      buildPreviewGraph(derived, {
        visuals,
        positionField: posField,
        cardSchema: nodeCardSchema,
        chain: config.chain,
      }),
    [derived, visuals, posField, nodeCardSchema, config.chain]
  );

  const [nodes, setNodes, onNodesChange] = useNodesState<FlowNodeType>(graph.nodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>(graph.edges);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Merge fresh data into existing nodes (preserve RF position/selection); add/drop by id.
  useEffect(() => {
    setNodes(prev =>
      graph.nodes.map(gn => {
        const ex = prev.find(p => p.id === gn.id);
        return ex ? { ...ex, data: gn.data } : gn;
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graph.nodes]);
  useEffect(() => setEdges(graph.edges), [graph.edges]); // eslint-disable-line react-hooks/exhaustive-deps

  const operations = useMemo(
    () => (getPath(mainForm, config.operationsPath) as Obj[]) ?? [],
    [mainForm, config.operationsPath]
  );
  const roleOf = useMemo(() => {
    const m = new Map<string, string>();
    for (const n of derived.nodes) m.set(n.id, n.role);
    return m;
  }, [derived.nodes]);
  const containerOf = useMemo(() => {
    const m = new Map<string, string | undefined>();
    for (const n of derived.nodes) m.set(n.id, n.container);
    return m;
  }, [derived.nodes]);

  const onNodeDragStop = useCallback(
    (_: unknown, node: Node<IFlowNodeData>) =>
      onChangeOperations(
        operations.map(op =>
          getPath(op, idField) === node.id
            ? setPath(op, posField, {
                x: Math.round(node.position.x),
                y: Math.round(node.position.y),
              })
            : op
        )
      ),
    [operations, idField, posField, onChangeOperations]
  );

  const isAllowed = useCallback(
    (source?: string, target?: string) => {
      const from = source ? roleOf.get(source) : undefined;
      const to = target ? roleOf.get(target) : undefined;
      if (!from || !to) return false;
      if (!config.rules.some(r => r.from === from && r.to === to)) return false;
      // Roles in the same chain-group must share the scoping container (e.g. the
      // packaging/batch nodes of the SAME package) — never mix lineages across
      // packages or active-substance rows.
      const groups = config.groups ?? {};
      if (groups[from] != null && groups[from] === groups[to])
        return containerOf.get(source!) === containerOf.get(target!);
      return true;
    },
    [config.rules, config.groups, roleOf, containerOf]
  );

  // Reconcile relatedNode across source MBOs to match the desired main-edge set:
  // keep/revive wanted, soft-delete persisted-but-unwanted, drop session-added,
  // add new — honoring the array soft-delete contract. Only touches MAIN edges
  // (entries whose target is a node); leaves anything else alone.
  const persist = useCallback(
    (desired: { source: string; target: string }[]) => {
      const bySrc = new Map<string, Set<string>>();
      for (const e of desired) {
        const set = bySrc.get(e.source) ?? new Set<string>();
        set.add(e.target);
        bySrc.set(e.source, set);
      }
      const next = operations.map(op => {
        const id = getPath(op, idField) as string;
        const want = bySrc.get(id) ?? new Set<string>();
        const raw = getPath(op, relField);
        const existing = (Array.isArray(raw) ? raw : raw ? [raw] : []) as Obj[];
        const out: Obj[] = [];
        const seen = new Set<string>();
        for (const e of existing) {
          const tid = getPath(e, relTgt) as string;
          if (!roleOf.has(tid)) {
            out.push(e); // not a main edge — leave untouched
            continue;
          }
          seen.add(tid);
          if (want.has(tid)) {
            const revived = { ...e };
            delete revived.toBeDeleted;
            out.push(revived);
          } else if (originalRelationIds?.has(String(e.id))) {
            out.push(setPath(e, 'toBeDeleted', true)); // soft-delete persisted
          }
          // else: session-added + unwanted → drop
        }
        for (const tid of want)
          if (!seen.has(tid)) out.push(setPath({ id: uuidv4() }, relTgt, tid));
        return existing.length || want.size ? setPath(op, relField, out) : op;
      });
      onChangeOperations(next);
    },
    [operations, idField, relField, relTgt, roleOf, originalRelationIds, onChangeOperations]
  );

  const currentEdges = useCallback(
    () => edges.map(e => ({ source: e.source, target: e.target })),
    [edges]
  );

  const onConnect = useCallback(
    (c: Connection) => {
      if (!c.source || !c.target || c.source === c.target) return;
      if (!isAllowed(c.source, c.target)) {
        message.warning(
          "Can't connect these — not allowed by the flow rules, or they belong to different packages."
        );
        return;
      }
      const desired = [
        ...currentEdges().filter(e => !(e.source === c.source && e.target === c.target)),
        { source: c.source, target: c.target },
      ];
      persist(desired);
    },
    [isAllowed, roleOf, currentEdges, persist, message]
  );

  const onEdgesDelete = useCallback(
    (deleted: Edge[]) => {
      const rm = new Set(deleted.map(e => `${e.source}->${e.target}`));
      persist(currentEdges().filter(e => !rm.has(`${e.source}->${e.target}`)));
    },
    [currentEdges, persist]
  );

  const selected = selectedId
    ? nodes.find(n => n.id === selectedId)?.data
    : undefined;

  return (
    <div style={{ display: 'flex', width: '100%', height: height ?? 600 }}>
      <div style={{ flex: 1, height: '100%', position: 'relative' }}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeDragStop={onNodeDragStop}
          onConnect={onConnect}
          onEdgesDelete={onEdgesDelete}
          onNodeClick={(_, n) => setSelectedId(n.id)}
          onPaneClick={() => setSelectedId(null)}
          nodesDraggable
          nodesConnectable
          elementsSelectable
          deleteKeyCode={['Backspace', 'Delete']}
          onlyRenderVisibleElements
          fitView
          fitViewOptions={{ maxZoom: 1, padding: 0.3 }}
          minZoom={0.2}
          proOptions={{ hideAttribution: true }}
          style={{ background: '#F9FAFD' }}
        >
          <Background />
          <Controls position="bottom-right" showInteractive={false} />
        </ReactFlow>
      </div>

      {selected && (
        <aside
          style={{
            width: panelWidth ?? 360,
            borderLeft: '1px solid #e2e8f0',
            background: '#ffffff',
            overflow: 'auto',
            padding: 16,
          }}
        >
          {panelSchema ? (
            <PanelDisplay schema={panelSchema} node={selected} />
          ) : (
            <>
              <h3 style={{ margin: '0 0 4px' }}>{selected.label}</h3>
              <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>
                {selected.category}
              </div>
              <SideOpList sides={selected.sides} visuals={visuals} />
            </>
          )}
        </aside>
      )}
    </div>
  );
};

/** Renders a configurable side-panel display schema against the node scope. */
const PanelDisplay = ({
  schema,
  node,
}: {
  schema: IComponentSchema<Record<string, unknown>>;
  node: IFlowNodeData;
}): ReactNode => {
  const scope = useMemo(
    () => ({ node, row: node.item, sides: node.sides }),
    [node]
  );
  const parsed = useMemo(
    () =>
      parseJSONSchema(
        toRuntimeTemplates(schema),
        scope
      ) as IComponentSchema<Record<string, unknown>>,
    [schema, scope]
  );
  return (
    <PageProvider value={{ context: scope, mode: PageMode.WORK }}>
      <ComponentRenderer schema={parsed} />
    </PageProvider>
  );
};

const SideOpList = ({
  sides,
  visuals,
}: {
  sides?: IFlowNodeData['sides'];
  visuals?: Record<string, IRoleVisual>;
}): ReactNode => {
  if (!sides?.length)
    return <div style={{ fontSize: 12, color: '#94a3b8' }}>No side operations.</div>;
  return (
    <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
      {sides.map((s, i) => {
        const v = visuals?.[s.role];
        const org = getPath(s.mbo ?? {}, 'manufacturerOrganization._formDisplayValue') as
          | string
          | undefined;
        return (
          <li
            key={`${s.id}-${i}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '6px 0',
              borderBottom: '1px solid #f1f5f9',
              fontSize: 13,
            }}
          >
            {v?.icon && (
              <span style={{ color: v.color, lineHeight: 1 }}>
                <AntIcon name={v.icon} />
              </span>
            )}
            <span style={{ flex: 1 }}>{v?.label ?? s.role}</span>
            {org && <span style={{ color: '#64748b', fontSize: 12 }}>{org}</span>}
          </li>
        );
      })}
    </ul>
  );
};

const PreviewFlowChart = (props: IPreviewFlowChartProps) => (
  <ReactFlowProvider>
    <PreviewInner {...props} />
  </ReactFlowProvider>
);

export default PreviewFlowChart;
